import {debounce, ensureErrorType, isNullOrEmpty} from "@2d-front-monorepo/core";
import React, {JSX, SyntheticEvent, useCallback, useId, useRef, useState} from "react";
import ListBox, {ListBoxItemProps} from "../../ListBox/ListBox";
import {GeoapifyAddressComponentProps} from "./GeoapifyAddressComponentProps";
import {GeoapifyResponseEntry} from "./GeoapifyResponseEntry";
import {generateUrl} from "./helpers";

interface GeoapifyResponse {
	results: GeoapifyResponseEntry[];
}

interface GeoapifyError {
	statusCode: number;
	message: string;
	error: string;
}

export default function GeoapifyAddressComponent(props: GeoapifyAddressComponentProps): JSX.Element {
	const {
		baseURL,
		apiKey,
		onSelect,
		onResponse,
		minChars,
		debounceTime,
		label,
		placeholder,
		autoFocus,
		language,
		bias,
		filter
	} = props;

	const id = useId();
	const inputRef = useRef<HTMLInputElement>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [listBoxItems, setListBoxItems] = useState<ListBoxItemProps<GeoapifyResponseEntry>[]>([]);
	const [listBoxVisible, setListBoxVisible] = useState<boolean>(false);

	const selectedCallback = useCallback((value: GeoapifyResponseEntry): void => {
		setLoading(false);
		setListBoxVisible(false);

		if (value == null) {
			return;
		}

		if (inputRef.current != null) {
			inputRef.current.value = value.formatted;
		}

		onSelect(value);

	}, [onSelect, setLoading, setListBoxVisible]);

	// Bumped on every input: a response whose generation no longer matches was superseded
	// (typed more, or cleared the field) and must not reopen the list with stale results.
	const requestGenerationRef = useRef(0);

	//https://apidocs.geoapify.com/docs/geocoding/address-autocomplete/#api
	const issueRequest = useCallback(debounce((generation: number, text: string) => {
			if (minChars != null && text.length < minChars) {
				// onInputChange already set loading: clear it, or the spinner spins forever.
				setLoading(false);
				setListBoxItems([{label: "Please enter at least " + minChars + " characters", value: null!}]);
				setListBoxVisible(true);
				return;
			}

			setLoading(true);
			setListBoxVisible(false);
			const url = generateUrl(baseURL, text, {
				lang: language,
				bias,
				filter
			}, apiKey);

			fetch(url, {credentials: "include"}).then(async response => {
					if (generation !== requestGenerationRef.current) {
						return;
					}
					setLoading(false);
					setListBoxVisible(true);

					try {
						const resp = await response.json();

						if (!response.ok) {
							const error = resp as GeoapifyError;
							return setListBoxItems([{label: error.message, value: null!}]);
						}

						let places = (resp as GeoapifyResponse).results;
						if (places.length === 0) {
							return setListBoxItems([{label: "No results found", value: null!}]);

						} else {
							if (onResponse) {
								places = onResponse(places);
							}

							setListBoxItems(places.map(item => ({
								label: item.formatted,
								value: item
							})));
						}

					} catch (e) {
						const error = ensureErrorType(e);
						setListBoxItems([{label: error.message, value: null!}]);
					}
				})
				.catch(e => {
					if (generation !== requestGenerationRef.current) {
						return;
					}
					setLoading(false);
					setListBoxVisible(true);
					setListBoxItems([{label: ensureErrorType(e).message, value: null!}]);
				});
		}, debounceTime ?? 350),
		[onResponse, language, bias, filter, setLoading, setListBoxVisible, minChars, debounceTime]
	);

	const onInputChange = useCallback((e: SyntheticEvent<HTMLInputElement>) => {
		const value = e.currentTarget.value;
		const generation = ++requestGenerationRef.current;

		if (isNullOrEmpty(value)) {
			setListBoxVisible(false);
			setLoading(false);

		} else {
			setLoading(true);
			issueRequest(generation, value);
		}
	}, [setListBoxVisible, setLoading, issueRequest]);

	return <div className={"labelled-field"}>
		<div className={"form__field form__field--autocomplete-input"}>
			<label className="field__label" htmlFor={id}>
				<span className="label__text">{label}</span>
			</label>
			<span className="field__input">
				<input type="search"
				       id={id}
				       ref={inputRef}
				       onInput={onInputChange}
				       placeholder={placeholder}
				       autoFocus={autoFocus}/>
				{loading && <span className="field__input__search--loading"></span>}
</span>
			{listBoxVisible && <ListBox items={listBoxItems} onSelect={selectedCallback}/>}
		</div>
	</div>;
};
